import React, { useEffect, useRef } from 'react';
import { Letter } from '@/types';
import { createLetterStyles, applyStyles } from '@/utils/animationUtils';

interface LetterDisplayProps {
  currentSequence: Letter[] | null; // CHANGED: Show multiple letters
  currentSequenceIndex: number; // ADDED: Which letter in sequence we're typing
  nextSequence?: Letter[] | null; // CHANGED: Preview next sequence
  className?: string;
}

export const LetterDisplay: React.FC<LetterDisplayProps> = ({
  currentSequence,
  currentSequenceIndex,
  nextSequence = null,
  className = ''
}) => {
  const currentLetterRef = useRef<HTMLDivElement>(null);

  /**
   * Apply styles based on current letter status
   */
  useEffect(() => {
    if (currentLetterRef.current && currentSequence && currentSequence[currentSequenceIndex]) {
      const element = currentLetterRef.current;
      const currentLetter = currentSequence[currentSequenceIndex];
      const styles = createLetterStyles[currentLetter.status];
      applyStyles(element, styles);
    }
  }, [currentSequence, currentSequenceIndex]);

  /**
   * Handle sequence transitions
   */
  useEffect(() => {
    if (currentLetterRef.current && currentSequence) {
      const element = currentLetterRef.current;

      // Add entrance animation
      element.style.transform = 'scale(0.8) translateY(10px)';
      element.style.opacity = '0';

      // Trigger entrance
      requestAnimationFrame(() => {
        element.style.transition = 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';
        element.style.transform = 'scale(1) translateY(0)';
        element.style.opacity = '1';
      });
    }
  }, [currentSequence]); // Trigger when sequence changes

  if (!currentSequence || currentSequence.length === 0) {
    return (
      <div className={`letter-display-container ${className}`}>
        <div className="letter-display-empty">
          <div className="text-6xl text-gray-500">
            Training Complete
          </div>
        </div>
      </div>
    );
  }

  const isPhrase = currentSequence.some(l => l.char === '_') || currentSequence.length > 12;

  return (
    <div className={`letter-display-container ${className}`}>
      {/* Current Sequence - Multiple Letters Display */}
      <div className="sequence-display-container">
        <div className={`sequence-letters ${isPhrase ? 'phrase-mode' : ''}`}>
          {currentSequence.map((letter, index) => {
            const isCurrentLetter = index === currentSequenceIndex;
            const isCompletedLetter = index < currentSequenceIndex;
            const isFutureLetter = index > currentSequenceIndex;

            return (
              <div
                key={letter.id}
                ref={isCurrentLetter ? currentLetterRef : null}
                id={isCurrentLetter ? `letter-${letter.id}` : undefined}
                className={`sequence-letter ${isCurrentLetter ? 'current' :
                    isCompletedLetter ? 'completed' : 'future'
                  }`}
                style={{
                  fontSize: isCurrentLetter ? '6rem' : '4rem',
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: isCurrentLetter ? '100px' : '80px',
                  minHeight: isCurrentLetter ? '100px' : '80px',
                  borderRadius: '12px',
                  userSelect: 'none',
                  margin: '0 8px',
                  transition: 'all 0.3s ease-out',
                  transform: isCurrentLetter ? 'scale(1)' : 'scale(0.8)',
                  opacity: isCurrentLetter ? 1 : isFutureLetter ? 0.6 : 0.8,
                  ...(isCurrentLetter ? createLetterStyles[letter.status] : {}),
                  ...(isCompletedLetter ? {
                    color: letter.status === 'correct' ? '#22c55e' : '#ef4444',
                    backgroundColor: 'rgba(0,0,0,0.1)'
                  } : {}),
                  ...(isFutureLetter ? {
                    color: '#9ca3af',
                    backgroundColor: 'rgba(0,0,0,0.05)'
                  } : {})
                }}
              >
                {letter.char === '_' ? (
                  <div
                    style={{
                      width: isCurrentLetter ? '60px' : '40px',
                      height: '8px',
                      backgroundColor: 'currentColor',
                      borderRadius: '4px'
                    }}
                  />
                ) : (
                  letter.char
                )}
              </div>
            );
          })}
        </div>

        {/* Progress indicator */}
        <div className="sequence-progress">
          <div className="progress-text">
            {currentSequenceIndex + 1} / {currentSequence.length}
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${((currentSequenceIndex) / currentSequence.length) * 100}%`
              }}
            />
          </div>
        </div>
      </div>

      {/* Next Sequence Preview (if available) */}
      {nextSequence && nextSequence.length > 0 && (
        <div className="next-sequence-preview">
          <div className="preview-label">Next:</div>
          <div className="preview-letters">
            {nextSequence.slice(0, 5).map((letter, index) => (
              <span
                key={letter.id}
                className="preview-letter"
                style={{
                  fontSize: '1.5rem',
                  fontFamily: 'monospace',
                  color: '#6b7280',
                  margin: '0 4px',
                  opacity: 1 - (index * 0.15)
                }}
              >
                {letter.char === '_' ? '⎵' : letter.char}
              </span>
            ))}
            {nextSequence.length > 5 && (
              <span className="preview-letter" style={{ color: '#9ca3af' }}>...</span>
            )}
          </div>
        </div>
      )}

      <style>{`
        .letter-display-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          position: relative;
        }

        .letter-display-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
        }

        .sequence-display-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          margin-bottom: 30px;
        }

        .sequence-letters {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          flex-wrap: wrap; /* Added flex-wrap for long phrases */
          max-width: 100%;
        }

        .sequence-letter {
          position: relative;
          z-index: 5;
        }

        /* Phrase Mode Styles */
        .sequence-letters.phrase-mode .sequence-letter.current {
          font-size: 3.5rem !important;
          min-width: 60px !important;
          min-height: 60px !important;
        }
        
        .sequence-letters.phrase-mode .sequence-letter:not(.current) {
          font-size: 2.5rem !important;
          min-width: 45px !important;
          min-height: 45px !important;
        }

        .sequence-letter.current {
          z-index: 10;
          position: relative;
        }

        .sequence-progress {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .progress-text {
          font-size: 0.875rem;
          color: #9ca3af;
          font-weight: 500;
        }

        .progress-bar {
          width: 200px;
          height: 4px;
          background: rgba(156, 163, 175, 0.3);
          border-radius: 2px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #22c55e);
          transition: width 0.3s ease-out;
          border-radius: 2px;
        }

        .next-sequence-preview {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          margin-top: 20px;
        }

        .preview-label {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .preview-letters {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        /* Animation keyframes */
        @keyframes letterEntrance {
          from {
            transform: scale(0.8) translateY(10px);
            opacity: 0;
          }
          to {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }

        @keyframes correctFlash {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        @keyframes incorrectShake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-2px); }
          75% { transform: translateX(2px); }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .sequence-letters {
            gap: 2px;
          }
          
          .sequence-letter {
            margin: 0 4px !important;
          }

          .sequence-letter.current {
            font-size: 4rem !important;
            min-width: 80px !important;
            min-height: 80px !important;
          }

          .sequence-letter:not(.current) {
            font-size: 3rem !important;
            min-width: 60px !important;
            min-height: 60px !important;
          }

          .sequence-letters.phrase-mode .sequence-letter.current {
            font-size: 2.5rem !important;
            min-width: 45px !important;
            min-height: 45px !important;
          }
          
          .sequence-letters.phrase-mode .sequence-letter:not(.current) {
            font-size: 1.8rem !important;
            min-width: 35px !important;
            min-height: 35px !important;
            margin: 0 2px !important;
          }
        }

        @media (max-width: 480px) {
          .sequence-letter.current {
            font-size: 3rem !important;
            min-width: 60px !important;
            min-height: 60px !important;
          }

          .sequence-letter:not(.current) {
            font-size: 2rem !important;
            min-width: 40px !important;
            min-height: 40px !important;
          }
          
          .sequence-letters.phrase-mode .sequence-letter.current {
            font-size: 2rem !important;
            min-width: 35px !important;
            min-height: 35px !important;
          }
          
          .sequence-letters.phrase-mode .sequence-letter:not(.current) {
            font-size: 1.5rem !important;
            min-width: 25px !important;
            min-height: 25px !important;
            margin: 0 1px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LetterDisplay;
